import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Eye, EyeOff, Trash2 } from "lucide-react"
import { CopyButton } from "@/components/ui/copy"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { ColumnDef } from "@tanstack/react-table"
import { Credential } from "@/types/api"
import { Table } from "@/components/ui/table"
import { formatDateTime } from "@/lib/utils/date"
import { useGenerateApiKey, useListApiKeys, useApiKeyDelete } from "@/hooks/useSettings"
import { useQueryClient } from "@tanstack/react-query"

export default function DeveloperSettings() {
  const queryClient = useQueryClient()

  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false)
  const [apiKeyName, setApiKeyName] = useState("")
  const [apiKeyDescription, setApiKeyDescription] = useState("")
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [keyToDelete, setKeyToDelete] = useState<Credential | null>(null)

  const { data: credentials, isLoading: credentialsLoading } = useListApiKeys()
  const generateApiKeyMutation = useGenerateApiKey()
  const deleteApiKeyMutation = useApiKeyDelete()

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(keyId)) {
        newSet.delete(keyId)
      } else {
        newSet.add(keyId)
      }
      return newSet
    })
  }

  const handleDelete = (credential: Credential) => {
    setKeyToDelete(credential)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (!keyToDelete) return
    deleteApiKeyMutation.mutate(keyToDelete.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["credentials"] })
        setDeleteDialogOpen(false)
        setKeyToDelete(null)
      },
    })
  }

  const columns: ColumnDef<Credential>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (info: any) => <span>{info.getValue() as string}</span>,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: (info: any) => <span className="text-xs text-gray-500">{info.getValue() as string}</span>,
    },
    {
      accessorKey: "credential",
      header: "API Key",
      cell: (info: any) => {
        const credential = info.getValue() as string
        const keyId = info.row.original.id
        const isVisible = visibleKeys.has(keyId)

        const maskApiKey = (key: string) => {
          if (key.length <= 8) return "•".repeat(key.length)
          return `${key.slice(0, 4)}${"•".repeat(key.length - 8)}${key.slice(-4)}`
        }

        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{isVisible ? credential : maskApiKey(credential)}</span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleKeyVisibility(keyId)
              }}
              className="rounded p-1 transition-colors hover:bg-gray-100"
              title={isVisible ? "Hide API key" : "Show API key"}
            >
              {isVisible ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
            </button>
            <div onClick={(e) => e.stopPropagation()}>
              <CopyButton text={credential} variant="ghost" size="icon" className="h-8 w-8" tooltip="Copy API key" />
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: (info: any) => <span className="font-mono text-xs">{formatDateTime(info.getValue() as string)}</span>,
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info: any) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(info.row.original)
              }}
              className="rounded p-1 transition-colors hover:bg-red-50 dark:hover:bg-gray-700"
              title="Delete API key"
              aria-label="Delete API key"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className="space-y-6 px-4">
        {/* API Keys Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <Button size="xs" onClick={() => setApiKeyModalOpen(true)}>
              New API Key
            </Button>
          </div>
          <Table
            data={credentials || []}
            columns={columns}
            isLoading={credentialsLoading}
            emptyMessage="No API keys found."
            className="divide-y divide-gray-200 dark:divide-gray-700"
            headerClassName="bg-gray-50 dark:bg-[#18181b]"
            bodyClassName="bg-white dark:bg-[#0F0F12] divide-y divide-gray-100 dark:divide-gray-800"
            cellClassName="py-2 text-sm"
          />
        </div>
      </div>

      <Dialog open={apiKeyModalOpen} onOpenChange={setApiKeyModalOpen}>
        <DialogContent className="max-w-xl overflow-visible p-0">
          <div className="flex items-start justify-between px-6 pt-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
                />
              </svg>
            </div>
          </div>
          <DialogHeader className="px-6 pt-1">
            <DialogTitle className="text-2xl font-semibold">Create API key</DialogTitle>
            <DialogDescription className="mt-2 text-sm">Create a new API key to access our API.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (!apiKeyName || generateApiKeyMutation.isPending) return
              generateApiKeyMutation.mutate(
                { name: apiKeyName, description: apiKeyDescription },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["credentials"] })
                    setApiKeyModalOpen(false)
                    setApiKeyName("")
                    setApiKeyDescription("")
                  },
                }
              )
            }}
          >
            <div className="mt-4 space-y-6 px-6">
              <Input label="Name" value={apiKeyName} onChange={(e) => setApiKeyName(e.target.value)} required />
              <Input
                label="Description (optional)"
                value={apiKeyDescription}
                onChange={(e) => setApiKeyDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
              <Button
                variant="ghost"
                type="button"
                onClick={() => setApiKeyModalOpen(false)}
                disabled={generateApiKeyMutation.isPending}
                size="xs"
                className="text-primary-600 bg-transparent px-3 font-normal shadow-none hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!apiKeyName || generateApiKeyMutation.isPending} className="min-w-[80px]">
                {generateApiKeyMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-xl overflow-visible p-0">
          <div className="flex items-start justify-between px-6 pt-6">
            <div className="rounded-lg border border-gray-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900">
              <Trash2 className="h-6 w-6 text-red-500" />
            </div>
          </div>
          <DialogHeader className="px-6 pt-1">
            <DialogTitle className="text-2xl font-semibold">Delete API key</DialogTitle>
            <DialogDescription className="mt-2 text-sm">
              Are you sure you want to delete this API key? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteApiKeyMutation.isPending}
              size="xs"
              className="text-primary-600 bg-transparent px-3 font-normal shadow-none hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="xs"
              onClick={confirmDelete}
              disabled={deleteApiKeyMutation.isPending}
              className="min-w-[80px] hover:bg-red-600 dark:hover:bg-red-700"
            >
              {deleteApiKeyMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
